import type { AccountResetPurpose } from '@sentra/contracts';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { Alert, AlertDescription } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { useAnonymousCsrfToken, useSessionContext } from '@/features/auth/session';

/**
 * Section 1.7's self-service reset, as three steps of the sign-in screen.
 *
 * **A step rather than a route, like every other unauthenticated screen in this product.** The
 * router never mounts for an anonymous visitor — `app/app.tsx` returns the sign-in page before it —
 * so the forced password change, the enrolment and the PIN setup are all screens returned in its
 * place rather than paths. This is the same, and it keeps the flow out of the URL entirely.
 *
 * **Nothing secret is ever in a URL, and that is why the code is typed rather than clicked.** There
 * is no link in the message: a link lands in a browser history, in a referrer and in whatever mail
 * scanner fetches it before the person does. A code is read off a phone and typed at the terminal,
 * which is also the device the operator is standing at.
 *
 * **The first step's answer is the same whatever the address was.** The server cannot tell us
 * whether an account exists — it does not look — so this screen must not imply that it did. The
 * wording says "if that address has an account", and it says so before the code box rather than
 * after, so nobody waits on a message that was never going to come.
 */

/** Which credential is being restored, which decides the second step and nothing else. */
export type ResetPurpose = AccountResetPurpose;

type Step = 'ask' | 'code' | 'done';

export function ResetFlow({
  purpose,
  onBack,
}: Readonly<{ purpose: ResetPurpose; onBack: () => void }>): ReactNode {
  const { t } = useTranslation();
  const { client } = useSessionContext();
  const describe = useApiErrorMessage();
  useAnonymousCsrfToken();

  const [step, setStep] = useState<Step>('ask');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function ask(): Promise<void> {
    setFailure(null);
    setSubmitting(true);
    try {
      await client.request(apiRoutes.authResetRequest, { body: { email, purpose } });
      setStep('code');
    } catch (error) {
      setFailure(describe(error));
    } finally {
      setSubmitting(false);
    }
  }

  async function redeem(): Promise<void> {
    setFailure(null);
    // Checked here as well as by the server, because the server never sees the second box: it is a
    // typing check rather than a rule, and telling somebody at the terminal is faster than a round
    // trip. The password's actual strength is the server's answer and is rendered from its code.
    if (purpose === 'password' && password !== repeat) {
      setFailure(t('auth.passwordsDoNotMatch'));
      return;
    }
    setSubmitting(true);
    try {
      if (purpose === 'password') {
        await client.request(apiRoutes.authResetPassword, {
          body: { code, newPassword: password },
        });
      } else {
        await client.request(apiRoutes.authResetTotp, { body: { code, password } });
      }
      setStep('done');
    } catch (error) {
      setFailure(describe(error));
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'done') {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm">
          {t(purpose === 'password' ? 'auth.reset.donePassword' : 'auth.reset.doneTotp')}
        </p>
        <Button type="button" onClick={onBack}>
          {t('auth.reset.backToSignIn')}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      data-testid="reset-flow"
      onSubmit={(event) => {
        event.preventDefault();
        void (step === 'ask' ? ask() : redeem());
      }}
      noValidate
    >
      {step === 'ask' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="reset-email">{t('auth.email')}</Label>
          <Input
            id="reset-email"
            name="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </div>
      )}

      {step === 'code' && (
        <>
          <Alert role="status">
            <AlertDescription>{t('auth.reset.sentIfKnown')}</AlertDescription>
          </Alert>

          <div className="flex flex-col gap-2">
            <Label htmlFor="reset-code">{t('auth.reset.code')}</Label>
            <Input
              id="reset-code"
              name="code"
              autoComplete="one-time-code"
              inputMode="text"
              required
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
              }}
            />
          </div>

          {purpose === 'password' ? (
            <>
              <div className="flex flex-col gap-2">
                <Label htmlFor="reset-new-password">{t('auth.newPassword')}</Label>
                <Input
                  id="reset-new-password"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="reset-repeat-password">{t('auth.repeatPassword')}</Label>
                <Input
                  id="reset-repeat-password"
                  name="repeatPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={repeat}
                  onChange={(event) => {
                    setRepeat(event.target.value);
                  }}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <Label htmlFor="reset-password">{t('auth.secretField')}</Label>
              <Input
                id="reset-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              <p className="text-xs text-muted-foreground">{t('auth.reset.totpNeedsPassword')}</p>
            </div>
          )}
        </>
      )}

      {failure !== null && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{failure}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" disabled={submitting}>
        {t(step === 'ask' ? 'auth.reset.sendCode' : 'auth.reset.confirm')}
      </Button>

      <Button type="button" variant="link" className="self-start px-0" onClick={onBack}>
        {t('auth.reset.backToSignIn')}
      </Button>
    </form>
  );
}
